import { useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';
export type SortValue = string | number | null | undefined;
export type SortColumns<T, K extends string> = Record<K, (row: T) => SortValue>;

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

const isMissing = (value: SortValue): value is null | undefined => value === null || value === undefined;

// A missing value sorts last whichever way the column is turned: a player who never joined has no
// deposits figure, which is not the same as depositing nothing, and must not top "lowest first".
export function sortRows<T>(rows: readonly T[], value: (row: T) => SortValue, direction: SortDirection): T[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = value(a);
    const right = value(b);
    if (isMissing(left) || isMissing(right)) return Number(isMissing(left)) - Number(isMissing(right));
    if (typeof left === 'number' && typeof right === 'number') return sign * (left - right);
    return sign * String(left).localeCompare(String(right));
  });
}

// `columns` must keep its identity between renders (declare it at module level), or every render re-sorts.
export function useSort<T, K extends string>(
  rows: readonly T[],
  columns: SortColumns<T, K>,
  initial: SortState<NoInfer<K>>,
) {
  const [sort, setSort] = useState(initial);
  const sorted = useMemo(() => sortRows(rows, columns[sort.key], sort.direction), [rows, columns, sort]);

  // Clicking the sorted column flips it; a new column starts from the end most worth seeing first.
  function toggle(key: K, first: SortDirection) {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: first },
    );
  }

  return { sorted, sort, toggle };
}
